type FlushFn = () => Promise<void>;
type BeaconFlushFn = () => void;

export class FlushScheduler {
  private intervalId: ReturnType<typeof setInterval> | null = null;
  private initialTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private readonly onFlush: FlushFn;
  private readonly onBeaconFlush: BeaconFlushFn;

  constructor(flush: FlushFn, beaconFlush: BeaconFlushFn) {
    this.onFlush = flush;
    this.onBeaconFlush = beaconFlush;
  }

  start(flushIntervalMs: number, initialFlushDelayMs?: number): void {
    // Guard against double-start: clear any existing interval/listeners first
    this.stop();

    // Fire an early first flush so late-arriving events don't wait a full interval.
    if (initialFlushDelayMs !== undefined && initialFlushDelayMs < flushIntervalMs) {
      this.initialTimeoutId = setTimeout(() => {
        this.onFlush().catch((err) => console.warn('[EventCollector] initial flush error', err));
      }, initialFlushDelayMs);
    }

    this.intervalId = setInterval(() => {
      this.onFlush().catch((err) => console.warn('[EventCollector] periodic flush error', err));
    }, flushIntervalMs);

    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    window.addEventListener('pagehide', this.handlePageHide);
    window.addEventListener('freeze', this.handleFreeze);
    window.addEventListener('resume', this.handleResume);
  }

  stop(): void {
    if (this.intervalId !== null) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    if (this.initialTimeoutId !== null) {
      clearTimeout(this.initialTimeoutId);
      this.initialTimeoutId = null;
    }
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    window.removeEventListener('pagehide', this.handlePageHide);
    window.removeEventListener('freeze', this.handleFreeze);
    window.removeEventListener('resume', this.handleResume);
  }

  private handleVisibilityChange = (): void => {
    if (document.visibilityState === 'hidden') {
      this.onBeaconFlush();
    } else {
      this.onFlush().catch((err) => console.warn('[EventCollector] visibility flush error', err));
    }
  };

  private handlePageHide = (): void => {
    this.onBeaconFlush();
  };

  private handleFreeze = (): void => {
    this.onBeaconFlush();
  };

  private handleResume = (): void => {
    this.onFlush().catch((err) => console.warn('[EventCollector] resume flush error', err));
  };
}
